import React, { forwardRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import SmartlistAPI from "../../api/SmartlistAPI";
import { FormProviderWithTranslations } from "../../contexts/FormTranslationsContext";
import { slugifyInput } from "../../utils/slug";
import { smartlistToApi } from "../../utils/smartlist";
import {
  apiToFormTranslations,
  createTranslationResponseToFormField,
  formToApiTranslations,
  getSmartlistTranslationsFormSchema,
} from "../../utils/translationAdapters/smartlist";
import Input from "../form/Input";
import MarkdownEditorField from "../form/MarkdownEditorField";
import DeletePicturesButton from "../formTranslations/DeletePicturesButton";
import FormPictures from "../formTranslations/FormPictures";
import FormTranslations from "../formTranslations/FormTranslations";
import FormTranslationsHeader from "../formTranslations/FormTranslationsHeader";

import type { LanguageCode } from "../../api/ProjectAPI.types";
import type {
  CreateTranslationResponse,
  GetSmartlistResponse,
  SmartlistTranslation,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { PictureKeys } from "../../constants/ratio";
import type {
  FormTranslationsHandle,
  PageFieldArray,
} from "../../contexts/FormTranslationsContext";
import type { SmartlistTranslationFormField } from "../../utils/translationAdapters/smartlist";
import type { UploadPictureState } from "../pictures/ModuleImageUploadWithTranslation";

export type SmartlistTranslationsHandle = FormTranslationsHandle<
  ReturnType<typeof formToApiTranslations>
>;

interface SmartlistTranslationsProps {
  smartlist: GetSmartlistResponse | UpdatedSmartlist;
  baseUrl: string;
  slugPath?: "smartlists" | "series" | "collections";
}

const SmartlistTranslations = forwardRef<
  SmartlistTranslationsHandle,
  SmartlistTranslationsProps
>(({ smartlist, baseUrl, slugPath = "smartlists" }, ref) => {
  const { t } = useTranslation("form");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const queryClient = useQueryClient();

  const getSmartlistQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSingleSmartlist,
    params: [{ smartlistId: smartlist.uuid, projectSlug }],
  });

  const methods = useForm<
    PageFieldArray<SmartlistTranslationFormField>,
    unknown,
    PageFieldArray<SmartlistTranslationFormField>
  >({
    mode: "onChange",
    resolver: zodResolver(getSmartlistTranslationsFormSchema()),
    defaultValues: {
      translations: apiToFormTranslations(smartlist.translations),
    },
  });

  const uploadPicture = useMutation({
    mutationFn: async ({
      pictureFile,
      translationId,
      pictureFormat,
    }: {
      pictureFile: File;
      translationId: string;
      pictureFormat: PictureKeys;
    }) =>
      SmartlistAPI.uploadPicture({
        smartlistId: smartlist.uuid,
        pictureFile,
        translationId,
        pictureFormat,
        projectSlug,
      }),
    onSuccess(data, variables) {
      const updatedTranslations = smartlist.translations.map((translation) => {
        if (translation.uuid === variables.translationId) {
          return { ...translation, picture: data.picture };
        }
        return translation;
      });

      const updatedSmartlist = {
        ...smartlist,
        translations: updatedTranslations,
      };

      queryClient.setQueryData(getSmartlistQuery.queryKey, updatedSmartlist);

      // Sync picture to RHF form
      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, data.picture);
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handlePictureUpload = ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }): void => {
    uploadPicture.mutate({ pictureFile: file, translationId, pictureFormat });
  };

  const uploadPictureState: UploadPictureState = {
    isLoading: uploadPicture.isLoading,
    pictureFormat: uploadPicture.variables?.pictureFormat,
    translationId: uploadPicture.variables?.translationId,
  };

  const deletePicture = useMutation({
    mutationFn: async ({ translationId }: { translationId: string }) =>
      SmartlistAPI.deletePicture({
        smartlistId: smartlist.uuid,
        translationId,
        projectSlug,
      }),
    onSuccess(_data, variables) {
      const updatedTranslations = smartlist.translations.map((translation) =>
        translation.uuid === variables.translationId
          ? { ...translation, picture: null }
          : translation,
      );

      queryClient.setQueryData(getSmartlistQuery.queryKey, {
        ...smartlist,
        translations: updatedTranslations,
      });

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, undefined);
      }

      toast.success(t("form:deletePicturesSuccess"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleAddLanguage = async ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }): Promise<CreateTranslationResponse | null> => {
    if (!languageUuid) return null;

    try {
      const response = await SmartlistAPI.createTranslation({
        smartlistId: smartlist.uuid,
        languageUuid,
        projectSlug,
      });

      const newTranslation: SmartlistTranslation = {
        uuid: response.uuid,
        default: response.default,
        short_description: response.short_description ?? "",
        description: response.description ?? "",
        language_uuid: response.language_uuid,
        language: languageCode,
        name: response.name ?? "",
        slug: response.slug ?? "",
        picture: null,
      };

      queryClient.setQueryData(getSmartlistQuery.queryKey, {
        ...smartlist,
        translations: [...smartlist.translations, newTranslation],
      });

      return response;
    } catch (error) {
      console.error("Error creating translation:", error);
      toast.error(t("genericError", { ns: "error" }));
      return null;
    }
  };

  const uptadeSmartlistMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: UpdatedSmartlist;
    }) =>
      SmartlistAPI.updateSmartlist({
        uuid,
        data: smartlistToApi(data),
        projectSlug,
      }),
    onSuccess: (updatedSmartlist) => {
      toast.success(t("form:deleteLanguageSuccess"));
      queryClient.setQueryData(getSmartlistQuery.queryKey, updatedSmartlist);
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleDeleteLanguage = async (
    updatedFormTranslations: ReturnType<typeof apiToFormTranslations>,
  ): Promise<void> => {
    const apiTranslations = formToApiTranslations(updatedFormTranslations);

    await uptadeSmartlistMutation.mutateAsync({
      uuid: smartlist.uuid,
      data: {
        ...smartlist,
        translations: apiTranslations.translations,
        translations_indexes: apiTranslations.translations_indexes,
      } as UpdatedSmartlist,
    });
  };

  return (
    <div className="bg-card flex flex-col rounded-[0.3125rem] p-[1.5625rem] drop-shadow-md">
      <FormProviderWithTranslations
        formToApiTranslations={formToApiTranslations}
        handleRef={ref}
        methods={methods}
        onAddLanguage={handleAddLanguage}
        onDeleteLanguage={handleDeleteLanguage}
        toFormField={createTranslationResponseToFormField}
      >
        <FormTranslationsHeader />
        <FormTranslations slugController="name">
          <Input
            isFullWidth
            isRequired
            label={t("fields.title")}
            name="name"
            type="text"
          />
          <Input
            isFullWidth
            label={t("fields.slug")}
            name="slug"
            normalize={slugifyInput}
            prefix={`${baseUrl}/${slugPath}/`}
            type="text"
          />
          <MarkdownEditorField
            isFullWidth
            label={t("form:fields.short_description")}
            name="short_description"
          />
          <MarkdownEditorField
            isFullWidth
            label={t("form:fields.description")}
            name="description"
          />
        </FormTranslations>
        <div>
          <div className="mb-0 flex items-center gap-[0.625rem]">
            <div className="font-semibold whitespace-nowrap md:whitespace-normal">
              {t("form:fields.pictures")} ({t("form:fields.picturesLimitation")}
              )
            </div>
            <DeletePicturesButton onDelete={deletePicture.mutateAsync} />
          </div>
          <FormPictures
            item={smartlist}
            itemType="smartlists"
            onUpload={handlePictureUpload}
            uploadPictureState={uploadPictureState}
          />
        </div>
      </FormProviderWithTranslations>
    </div>
  );
});

SmartlistTranslations.displayName = "SmartlistTranslations";

export default SmartlistTranslations;
